import { useState, useCallback, useEffect, useRef } from 'react';
import { ReactFlowProvider } from 'reactflow';
import { Toaster } from 'react-hot-toast';
import { AlertCircle } from 'lucide-react';
import Canvas from './components/Canvas';
import Sidebar from './components/Sidebar';
import Properties from './components/Properties';
import Toolbar from './components/Toolbar';
import SearchModal from './components/modals/SearchModal';
import UserMessagesModal from './components/modals/UserMessagesModal';
import useFlowStore from './stores/useFlowStore';
import { useAuth } from './contexts/AuthContext';
import { createNode } from './utils/helpers';
import { userMessagesApi } from './utils/api';

function App() {
  const [showSidebar, setShowSidebar] = useState(true);
  const [showProperties, setShowProperties] = useState(true);
  const [showSearch, setShowSearch] = useState(false);
  const [pendingMessages, setPendingMessages] = useState([]);
  const { theme, addNode } = useFlowStore();
  const { userHasRole, user, licenseExpired, token } = useAuth();
  const reactFlowWrapper = useRef(null);

  // Fetch pending system messages only after the access token is ready.
  // Using `token` (not `authenticated`) ensures the axios interceptor is
  // already wired up before this API call goes out, preventing a spurious
  // 401 that would otherwise trigger the "session expired" redirect loop.
  // The `messagesChecked` ref ensures we only fetch once per session even
  // though `token` is refreshed periodically.
  const messagesChecked = useRef(false);
  useEffect(() => {
    if (!token || messagesChecked.current) return;
    messagesChecked.current = true;
    userMessagesApi.getPending()
      .then(res => { if (res?.data?.length) setPendingMessages(res.data); })
      .catch(() => {}); // Silently ignore — non-critical feature
  }, [token]);

  // Keyboard shortcut for search (Ctrl+K or Cmd+K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        // Only open search if there's an active model
        const state = useFlowStore.getState();
        if (state.modelName && state.modelName.trim() !== '') {
          setShowSearch(true);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Apply theme to document
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // Handle drag and drop from sidebar
  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const reactFlowBounds = reactFlowWrapper.current.getBoundingClientRect();
      const type = event.dataTransfer.getData('application/reactflow');

      if (!type) return;

      // Check if there's an active model
      const state = useFlowStore.getState();
      if (!state.modelName || state.modelName.trim() === '') {
        toast.error('Please create or open a model before adding entities');
        return;
      }

      const position = {
        x: event.clientX - reactFlowBounds.left - 125, // Center the node
        y: event.clientY - reactFlowBounds.top - 60,
      };

      const newNode = createNode(type, position, 'New Entity', state.nodes);
      addNode(newNode);
    },
    [addNode]
  );

  return (
    <div className={`h-screen flex flex-col ${theme === 'dark' ? 'dark' : ''}`}>
      <Toaster
        position="bottom-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: theme === 'dark' ? '#1f2937' : '#fff',
            color: theme === 'dark' ? '#fff' : '#000',
          },
        }}
      />

      <Toolbar
        onToggleSidebar={() => setShowSidebar(!showSidebar)}
        onToggleProperties={() => setShowProperties(!showProperties)}
        onToggleSearch={() => setShowSearch(true)}
        showProperties={showProperties}
      />

      {/* Warning Banner — expired license */}
      {user && licenseExpired && (
        <div className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800 px-4 py-3">
          <div className="flex items-center gap-3 max-w-7xl mx-auto">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-800 dark:text-red-200">
                License Expired — Read-only mode
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-0.5">
                Your license expired on {user.expireDate}. Saving is disabled. Contact support to renew your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Warning Banner — no subscription */}
      {user && !userHasRole && !licenseExpired && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border-b border-yellow-200 dark:border-yellow-800 px-4 py-3">
          <div className="flex items-center gap-3 max-w-7xl mx-auto">
            <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                Limited Access - Subscription Required
              </p>
              <p className="text-xs text-yellow-700 dark:text-yellow-300 mt-0.5">
                You need an active subscription to save your models. You can view, create and edit locally, but you won't be able to save to the database. Contact support to activate your subscription.
              </p>
            </div>
          </div>
        </div>
      )}

      <ReactFlowProvider>
        <div className="flex-1 flex overflow-hidden">
          <Sidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

          <div
            ref={reactFlowWrapper}
            className="flex-1 bg-gray-50 dark:bg-gray-900"
            onDrop={onDrop}
            onDragOver={onDragOver}
          >
            <Canvas />
            {showSearch && <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />}
          </div>

          <Properties isOpen={showProperties} onClose={() => setShowProperties(false)} />
        </div>
      </ReactFlowProvider>

      {/* System messages — shown after login, cannot be dismissed without reading */}
      {pendingMessages.length > 0 && (
        <UserMessagesModal
          messages={pendingMessages}
          onAllRead={() => setPendingMessages([])}
        />
      )}
    </div>
  );
}

export default App;
